import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash } from '@phosphor-icons/react';
import { OptionRowsSkeleton } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useToast } from '@/hooks/use-toast';
import {
  useGetEmployeesLookup,
  useGetSurveyFormsLookup,
  useSendSurvey,
} from '../../hooks/use-surveys';
import { EmployeesLookupResult, SurveyFormOption } from '../../types/survey.types';

export interface SendSurveyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SendSurveyDialog = ({ open, onOpenChange }: SendSurveyDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [surveyFormId, setSurveyFormId] = useState('');
  const [deadline, setDeadline] = useState('');
  const [recipients, setRecipients] = useState<{ id: string; name: string; email: string }[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const { data: formsData, isLoading: isFormsLoading } = useGetSurveyFormsLookup();
  const forms =
    (formsData as { SurveyForms?: { items: SurveyFormOption[] } })?.SurveyForms?.items ?? [];

  const { data: employeesData, isLoading: isEmployeesLoading } = useGetEmployeesLookup('');
  const employees = (employeesData as { Users?: EmployeesLookupResult })?.Users?.items ?? [];

  const { mutateAsync: sendSurvey, isPending } = useSendSurvey();

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setSurveyFormId('');
    setDeadline('');
    setRecipients([]);
  }, [open]);

  const addRecipient = () => {
    const employee = employees.find((option) => option.ItemId === selectedEmployeeId);
    if (!employee || recipients.some((r) => r.id === employee.ItemId)) return;
    const name = employee.DisplayName ?? `${employee.FirstName} ${employee.LastName}`.trim();
    setRecipients([...recipients, { id: employee.ItemId, name, email: employee.Email }]);
    setSelectedEmployeeId('');
  };

  const removeRecipient = (id: string) => {
    setRecipients(recipients.filter((r) => r.id !== id));
  };

  const handleSend = async () => {
    if (!title || !surveyFormId || recipients.length === 0) {
      toast({ title: t('TITLE_FORM_AND_RECIPIENTS_REQUIRED'), variant: 'destructive' });
      return;
    }
    try {
      await sendSurvey({
        Title: title,
        Description: description,
        SurveyFormId: surveyFormId,
        Deadline: deadline || undefined,
        Recipients: recipients.map((r) => ({ UserId: r.id, UserName: r.name, UserEmail: r.email })),
      });
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('SEND_SURVEY')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('TITLE')}</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('TITLE')}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('DESCRIPTION')}</Label>
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('DESCRIPTION')}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('SURVEY_FORM')}</Label>
            <Select value={surveyFormId} onValueChange={setSurveyFormId}>
              <SelectTrigger>
                <SelectValue placeholder={t('SURVEY_FORM')} />
              </SelectTrigger>
              <SelectContent>
                {isFormsLoading && <OptionRowsSkeleton />}
                {forms.map((form) => (
                  <SelectItem key={form.ItemId} value={form.ItemId}>
                    {form.Title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>{t('DEADLINE_OPTIONAL')}</Label>
            <DatePicker value={deadline} onChange={setDeadline} className="w-full" />
          </div>

          <div className="space-y-2">
            <Label>{t('RECIPIENTS')}</Label>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('EMPLOYEE')} />
                  </SelectTrigger>
                  <SelectContent>
                    {isEmployeesLoading && <OptionRowsSkeleton />}
                    {employees.map((employee) => (
                      <SelectItem key={employee.ItemId} value={employee.ItemId}>
                        {employee.DisplayName ??
                          `${employee.FirstName} ${employee.LastName}`.trim()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addRecipient}
                disabled={!selectedEmployeeId}
              >
                {t('ADD')}
              </Button>
            </div>
            {recipients.map((recipient) => (
              <div
                key={recipient.id}
                className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
              >
                <span>{recipient.name}</span>
                <Button variant="ghost" size="icon" onClick={() => removeRecipient(recipient.id)}>
                  <Trash className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <DialogCancelButton onClick={() => onOpenChange(false)}>{t('CANCEL')}</DialogCancelButton>
          <Button type="button" onClick={handleSend} loading={isPending} disabled={isPending}>
            {t('SEND')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
